import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search, MapPin } from 'lucide-react';
import { Drawer } from 'vaul';
import { Nusach, Shul } from '../../types';
import { DEFAULT_AREA_ID, getArea } from '../../config/areas';
import { filterAndSortShuls, shulDistance } from '../../utils/shulSearch';
import { LatLng, hasCoords, navTargetOf } from '../../utils/geo';
import DistanceText from '../directory/DistanceText';
import { useIsMobile } from '../../hooks/useIsMobile';
import LazyDirectoryMap from '../directory/map/LazyDirectoryMap';
import type { MapItem } from '../directory/map/types';
import NavigateButton from '../directory/NavigateButton';

interface ShulMapViewProps {
  shuls: Shul[];
  loading: boolean;
  error: string | null;
  focusShulId?: string | null;
  /** The user's location after Near me. */
  origin?: LatLng | null;
  /** Near me messages, shown above the list. */
  notices?: React.ReactNode;
}

const MAP_SHADOW = 'shadow-[0_2px_6px_rgba(0,0,0,.04),0_4px_8px_rgba(0,0,0,.10)]';
const SNAP_POINTS = [0.18, 0.5, 0.92] as const;
const DEFAULT_SNAP = SNAP_POINTS[1];

function getNusachim(shul: Shul): Nusach[] {
  return shul.nusach ? [shul.nusach] : [];
}

export default function ShulMapView({
  shuls,
  loading,
  error,
  focusShulId = null,
  origin = null,
  notices,
}: ShulMapViewProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(focusShulId);
  const [snap, setSnap] = useState<number | string | null>(DEFAULT_SNAP);
  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (focusShulId) setSelectedId(focusShulId);
  }, [focusShulId]);

  const filtered = useMemo(() => filterAndSortShuls(shuls, query, isHebrew, origin), [shuls, query, isHebrew, origin]);
  const mappable = useMemo(() => filtered.filter((s) => hasCoords(s)), [filtered]);
  const items = useMemo<MapItem[]>(
    () =>
      mappable.map((s) => ({
        id: s.id,
        lat: s.lat,
        lng: s.lng,
        approximate: false,
        category: 'shul',
        title: isHebrew ? s.nameHe : s.nameEn,
      })),
    [mappable, isHebrew]
  );
  const area = getArea(DEFAULT_AREA_ID);
  const navTargetFor = (shul: Shul) =>
    navTargetOf(
      { addressEn: shul.address, addressHe: '', lat: shul.lat, lng: shul.lng, areaId: DEFAULT_AREA_ID, showExactLocation: true },
      area,
      isHebrew
    );

  useEffect(() => {
    if (!selectedId) return;
    rowRefs.current[selectedId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedId]);

  useEffect(() => {
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile]);

  const toggleSelect = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  if (loading) {
    return <div className="h-[70vh] rounded-[16px] bg-fog animate-pulse" />;
  }

  if (error) {
    return <p className="text-center text-sm text-danger-500 py-8">{t('common.error')}</p>;
  }

  const searchInput = (
    <div className="relative">
      <Search className="absolute top-1/2 -translate-y-1/2 start-4 w-4 h-4 text-ash pointer-events-none" />
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('common.search')}
        className={`w-full ps-10 pe-4 py-2.5 rounded-full border border-ink/15 bg-paper text-ink placeholder:text-ash focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm ${MAP_SHADOW}`}
      />
    </div>
  );

  const listRows = (
    <>
      {notices}
      {mappable.length === 0 && (
        <p className="text-center text-sm text-ash py-8">{t('common.noResults')}</p>
      )}
      {mappable.map((shul) => {
        const isSelected = shul.id === selectedId;
        const nusachim = isSelected ? getNusachim(shul) : [];
        const distance = origin ? shulDistance(shul, origin) : undefined;

        return (
          <button
            key={shul.id}
            type="button"
            ref={(el) => {
              rowRefs.current[shul.id] = el;
            }}
            onClick={() => toggleSelect(shul.id)}
            className={`w-full text-start p-3 rounded-[16px] border transition-colors ${
              isSelected
                ? 'border-weekday-600 bg-weekday-50'
                : 'border-ink/10 bg-paper hover:border-ink/30'
            }`}
          >
            <p className="font-semibold text-ink text-sm truncate">
              {isHebrew ? shul.nameHe : shul.nameEn}
            </p>
            {(shul.address || distance !== undefined) && (
              <p className="flex items-center gap-1 text-xs text-ash mt-0.5 truncate">
                <MapPin className="w-3 h-3 shrink-0" />
                {distance !== undefined && <DistanceText meters={distance} className="font-medium text-ink" />}
                {distance !== undefined && shul.address && ' · '}
                <span className="truncate">{shul.address}</span>
              </p>
            )}
            {isSelected && (
              <>
                {nusachim.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {nusachim.map((n) => (
                      <span
                        key={n}
                        className="text-xs px-2 py-0.5 rounded-[10px] bg-fog text-ink font-medium"
                      >
                        {t(`nusach.${n}`)}
                      </span>
                    ))}
                  </div>
                )}
                <Link
                  to={`/shul/${shul.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-block mt-3 text-xs font-medium bg-ink text-paper rounded-full px-3 py-1.5 hover:opacity-90 transition-opacity"
                >
                  {t('home.diveIn')}
                </Link>
              </>
            )}
          </button>
        );
      })}
    </>
  );

  const bottomInset = isMobile && typeof snap === 'number' ? Math.round(snap * (window.innerHeight - 64)) : 0;

  const map = (
    <LazyDirectoryMap
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      userLocation={origin}
      bottomInset={bottomInset}
      topInset={isMobile ? 60 : 0}
      zoomButtons={!isMobile}
      renderPopup={(id) => {
        const shul = mappable.find((s) => s.id === id);
        if (!shul) return null;
        return (
          <div className="space-y-1">
            <p className="font-semibold text-ink text-sm">{isHebrew ? shul.nameHe : shul.nameEn}</p>
            {shul.address && <p className="text-xs text-ash">{shul.address}</p>}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Link
                to={`/shul/${shul.id}`}
                className="text-xs font-medium !text-weekday-700 hover:underline"
              >
                {t('home.diveIn')}
              </Link>
              <NavigateButton target={navTargetFor(shul)} variant="compact" />
            </div>
          </div>
        );
      }}
    />
  );

  if (isMobile) {
    return (
      <div className="fixed inset-x-0 top-[var(--nav-bottom)] bottom-0 z-20">
        <div className="absolute inset-0">{map}</div>

        <Drawer.Root
          open
          modal={false}
          dismissible={false}
          snapPoints={[...SNAP_POINTS]}
          activeSnapPoint={snap}
          setActiveSnapPoint={setSnap}
        >
          <Drawer.Portal>
            <Drawer.Content
              className={`fixed inset-x-0 bottom-0 z-30 flex h-full flex-col rounded-t-[16px] bg-paper outline-none ${MAP_SHADOW}`}
            >
              <Drawer.Handle
                preventCycle
                className="mx-auto mt-2 mb-1 !h-6 !w-full !max-w-none !bg-transparent opacity-100 rounded-none"
              >
                <span className="mx-auto block w-10 h-1 rounded-full bg-ink/20" />
              </Drawer.Handle>
              <Drawer.Title className="sr-only">{t('home.shulsHeading')}</Drawer.Title>
              <div className="px-4 pb-2 shrink-0">{searchInput}</div>
              <div
                className="flex-1 overflow-y-auto px-4 pb-6 space-y-2 overscroll-contain"
                data-vaul-no-drag
              >
                {listRows}
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="sm:w-2/5 sm:order-2 space-y-2">
        {searchInput}
        <div className="space-y-2 max-h-[calc(70vh-3rem)] overflow-y-auto">
          {listRows}
        </div>
      </div>

      <div className="relative sm:w-3/5 sm:order-1 h-[50vh] sm:h-[70vh] rounded-[16px] overflow-hidden border border-ink/10">
        {map}
      </div>
    </div>
  );
}
